import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { Grid2, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo } from "react";
import { useSearchCampaignsByEventId } from "../../hooks/useSearchEvents";
import { CampaignEventData } from "../../models/CampaignEventData";
import { useTableData } from "./useTableData";

export const CampaignDataTable = ({
	selectedEventId,
	setError,
}: {
	selectedEventId: number;
	setError: (error: string) => void;
}) => {
	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useSearchCampaignsByEventId({
		eventId: selectedEventId,
		isDraftMode: false,
	});

	const { defaultColDef } = useGridDefinitions({});
	const { columnDefs } = useTableData();

	const { pinnedTopRowData, rowData, pinnedBottomRowData } = useMemo(() => {
		const pinnedTop: CampaignEventData[] = [];
		const regular: CampaignEventData[] = [];
		const pinnedBottom: CampaignEventData[] = [];

		data?.campaigns?.forEach((row) => {
			if (row.HeatNumber?.startsWith("Roll change")) {
				pinnedTop.push(row);
			} else if (row.IsTotal === 1) {
				pinnedBottom.push(row);
			} else {
				regular.push(row);
			}
		});

		return {
			pinnedTopRowData: pinnedTop,
			rowData: regular,
			pinnedBottomRowData: pinnedBottom,
		};
	}, [data]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<Grid2
			size={{ md: 12 }}
			sx={{
				height: rowData.length > 0 ? "35vh" : "0",
			}}
			component={Paper}
		>
			<Grid2 container spacing={1} size={12} sx={{ height: "100%" }} p={2}>
				<Grid2 size={{ xs: 12 }}>
					<Typography variant="h6" fontWeight={600} fontSize={"1rem"}>
						Campaign Info
					</Typography>
				</Grid2>
				{rowData.length === 0 && (
					<Grid2 size={{ xs: 12 }}>
						<Typography variant="caption" color="text.secondary">
							No campaign info
						</Typography>
					</Grid2>
				)}
				<Grid2 size={{ xs: 12 }} height="90%">
					<AgGridReact<CampaignEventData>
						loading={isLoading}
						rowData={rowData}
						pinnedTopRowData={pinnedTopRowData}
						pinnedBottomRowData={pinnedBottomRowData}
						rowHeight={35}
						headerHeight={30}
						animateRows={true}
						defaultColDef={defaultColDef}
						columnDefs={columnDefs}
						loadingOverlayComponent={CenteredLazyLoading}
						overlayNoRowsTemplate={"-"}
						rowSelection="multiple"
						suppressContextMenu={false}
					/>
				</Grid2>
			</Grid2>
		</Grid2>
	);
};
